import classnames from 'classnames';
import ButtonGroupOverlay from 'components/ButtonGroupOverlay';
import { SelectOption } from 'components/inputs/BaseSelect';
import ShortDropdown from 'components/inputs/dropdowns/ShortDropdown';
import SearchInput from 'components/inputs/SearchInput';
import download from 'images/icons/download_white.svg';
import Immutable from 'immutable';
import React from 'react';
import { ValueType } from 'react-select/src/types';
import { Doc } from 'records/Doc';
import { Submittal, SubmittalId, SubmittalOrderedMap } from 'records/Submittal';
import { downloadSubmittalAttachment } from 'services/submittalService';
import { downloadFileWithAnchor, getDateString } from 'utils/general';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
// import BasicFilter from 'components/Icons/BasicFilter';
import { useTranslator } from '@bluebeam/react-i18n';
import SortButton, { ApprovedFileSortFields } from './SortButton';

interface Props extends WithAppStoreProps {
  approvedSubmittals: Immutable.OrderedMap<SubmittalId, Submittal>;
  approvedSubmittalGroups: Immutable.OrderedMap<string, SubmittalOrderedMap>;
  selectedSpecSection: string;
  selectedSpecNumber: string | null;
  sortField: ApprovedFileSortFields;
  sortDirection: 'ascending' | 'descending';
  onSpecSectionChange: (key: string) => void;
  onSearchChange: (search: string) => void;
  onSortChange: (
    filed: ApprovedFileSortFields,
    direction: 'ascending' | 'descending'
  ) => void;
  handleDownloadingState: (s: boolean) => void;
}

const ApprovedSubHeader = (props: Props) => {
  const { translate } = useTranslator([
    'specSections',
    'submittalsPage',
    'formLabels',
    'buttons',
  ]);

  const handleSpecFolderChange = (
    selectedOption: ValueType<SelectOption, boolean>
  ): void => {
    if (selectedOption) {
      props.onSpecSectionChange((selectedOption as SelectOption).value);
    }
  };

  const getSpecFolderOption = (value: string): SelectOption => {
    const specType: `${string}:${string}` =
      `specSections:${value}` as `${string}:${string}`;
    return {
      label:
        value === 'all'
          ? translate('specSections:allFolders')
          : `${value} - ${translate(specType)}`,
      value,
    };
  };

  const getSpecFolderOptions = (): any[] => {
    const { approvedSubmittals } = props;

    const specSectionsUsed = approvedSubmittals
      .valueSeq()
      .map((s) => s.specsectionnumber.slice(0, 2))
      .toOrderedSet()
      .sort();

    const options = specSectionsUsed.map(getSpecFolderOption).toArray();
    const AllSpecSectionsOption = {
      label: translate('specSections:allFolders'),
      value: 'all',
    };

    options.unshift(AllSpecSectionsOption);

    return options;
  };

  const display = (): boolean => {
    const { selectedApprovedFiles } = props.appState;
    return selectedApprovedFiles.size > 0;
  };

  const handleDeselectMulti = (): void => {
    props.actions.setSelectedApprovedFiles(Immutable.Set<string>());
  };

  const downloadPDF = async (doc: Doc): Promise<void> => {
    try {
      // setState({ downloading: true });
      // const { doc } = this.props;
      if (doc) {
        const url = await downloadSubmittalAttachment(
          doc.projectid,
          doc.documentid
        );

        const filename = `${doc.filename.replace(
          /\.[^/.]+$/,
          ''
        )}-${getDateString()}.pdf`;

        await downloadFileWithAnchor(url, filename);
      }
    } catch (err) {
      console.log('SubmittalViewerModal downloadPDF Error', err);
    } finally {
      props.handleDownloadingState(true);
    }
  };

  const handleDownloadSelected = async (): Promise<void> => {
    const { selectedApprovedFiles, documents } = props.appState;
    props.handleDownloadingState(false);

    const selectedDocs = documents
      .filter((d) => selectedApprovedFiles.includes(d.documentid))
      .valueSeq()
      .toArray();

    await Promise.all(selectedDocs.map(downloadPDF));
    props.handleDownloadingState(true);
  };

  const {
    approvedSubmittals,
    selectedSpecSection,
    selectedSpecNumber,
    approvedSubmittalGroups,
    onSearchChange,
    appState,
  } = props;
  const { selectedApprovedFiles } = appState;

  const noApprovedSubs = approvedSubmittals.size <= 0;

  const subGroup = approvedSubmittalGroups.get(selectedSpecNumber || '');

  const hasFiles = subGroup && subGroup.size > 0;

  return (
    <div id="approved-subheader">
      <div className="column mx4">
        <div className="hs2 my3 row items-center justify-between">
          {display() ? (
            <ButtonGroupOverlay
              components={[
                <div>
                  {`(${selectedApprovedFiles.size}) ${translate(
                    'submittalsPage:itemsSelected'
                  )}`}
                </div>,
                <div
                  className="cursor-pointer"
                  onClick={handleDownloadSelected}
                >
                  <img className="mr1" src={download} alt="trash icon" />
                  {translate('buttons:download')}
                </div>,
                <div className="cursor-pointer" onClick={handleDeselectMulti}>
                  {translate('buttons:deselect')}
                </div>,
              ]}
            />
          ) : null}
          <div
            className={classnames({ 'cursor-not-allowed': noApprovedSubs })}
            style={{ width: '336px' }}
          >
            <ShortDropdown
              id="approved-subheader-spec-folder-select"
              isMulti={false}
              isDisabled={noApprovedSubs}
              isSearchable
              value={getSpecFolderOption(selectedSpecSection)}
              options={getSpecFolderOptions()}
              menuPlacement="auto"
              onChange={handleSpecFolderChange}
            />
          </div>
          <div className="row">
            <SortButton
              sortOptions={[
                [translate('submittalsPage:dateApproved'), 'dateapproved'],
                [translate('submittalsPage:category'), 'category'],
                [
                  translate('submittalsPage:reviewerResponseTitle'),
                  'reviewerresponse',
                ],
                [translate('formLabels:firstName'), 'firstname'],
              ]}
              startingSortField={props.sortField}
              startingSortDirection={props.sortDirection}
              handleApplySort={props.onSortChange}
            />
            <SearchInput
              disabled={!hasFiles}
              id="approved-subheader-search"
              placeholder={translate('buttons:search')}
              onChange={onSearchChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAppStore<Props>(ApprovedSubHeader);
